import React, { useEffect, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { isValidNotNilUuid } from '../../Constants/StaticUUIDs';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import CurrentUserContext from '../../Context/CurrentUserContext';

import GatewayReminderInstanceService from '../../Services/GatewayReminderInstanceService';
import RuleService from '../../Services/RuleService';
import SubscriptionService from '../../Services/SubscriptionService';

import AssetSubscriptionsDisplay from './AssetSubscriptionsDisplay';

const limit = 100;

const initState = {
  maintenanceState: { count: 0, instances: [] },
  ruleState: { Count: 0, Rules: [] },
  selectedUser: {},
  showSubscribeNonPlatformUserModal: false,
  subscriptionPending: true,
  subscriptions: new Map(),
};

export default function AssetSubscriptionsContainer(props) {
  const {
    asset: {
      _id: gatewayIdFilter,
      orgID,
      gtemplate,
      gtemplateID: sourceIdFilter,
    },
    handleSubscriptionAlert,
  } = props;

  const currentOrg = useContext(CurrentOrgContext) || {};
  const currentUser = useContext(CurrentUserContext) || {};

  const [state, updateState] = useState(initState);
  const [error, setError] = useState('');

  const currentUserRef = useRef(null);

  const reminderInstanceService = useMemo(
      () => new GatewayReminderInstanceService(),
      []
    ),
    ruleService = useMemo(() => new RuleService(), []),
    subscriptionService = useMemo(() => new SubscriptionService(), []);
  const source = CancelToken.source();

  const commonMaintenanceParams = useMemo(
      () => ({ gateway_id: gatewayIdFilter, limit }),
      [gatewayIdFilter]
    ),
    commonRulesParams = useMemo(
      () => ({ orgID, orgIdFilter: gtemplate?.orgID ?? orgID }),
      [gtemplate?.orgID, orgID]
    ),
    commonSubscriptionsParams = useMemo(
      () => ({ orgID, orgIdFilter: orgID }),
      [orgID]
    );

  const maintenanceReminderIdSet = useMemo(
    () =>
      new Set(
        state.maintenanceState.instances?.map(
          (instance) => instance?.GtemplateReminder?._id
        )
      ),
    [state.maintenanceState.instances]
  );

  const fetchAllReminderInstances = async () => {
    try {
      const params = { ...commonMaintenanceParams, page: 1 };
      const { count, data } =
        await reminderInstanceService.getPaginatedGatewayReminderInstances(
          params,
          source.token
        );

      if (data.length <= count) return { count, instances: data };

      const additionalRequests = Math.ceil(count / limit) - 1;
      const requestsArray = new Array(additionalRequests)
        .fill(null)
        .map((_, i) =>
          reminderInstanceService.fetchAssets(
            { ...commonMaintenanceParams, page: i + 2 },
            source.token
          )
        );
      const responses = await Promise.all(requestsArray);
      const additionalData = responses.reduce((flatData, { data }) => {
        flatData.push(...data);
        return flatData;
      }, []);

      return { count, instances: data.concat(additionalData) };
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      return { count: 0, instances: [] };
    }
  };

  const fetchAllTemplateRules = async () => {
    try {
      const rulesParams = { ...commonRulesParams, sourceIdFilter };
      const { Count } = await ruleService.fetchRulesMetrics(
        rulesParams,
        source.token
      );
      if (!Count) return { Count: 0, Rules: [] };

      const rulesRequestCount = Math.ceil(Count / limit);
      const rulesRequests = new Array(rulesRequestCount).fill(1).map((x, i) => {
        return ruleService.fetchPaginatedRules(
          { ...rulesParams, limit, page: i + 1 },
          source.token
        );
      });
      const responses = await Promise.all(rulesRequests);
      const Rules = responses.reduce((allRules, { Rules: subSet }) => {
        if (!Array.isArray(subSet)) return allRules;
        allRules.push(...subSet);
        return allRules;
      }, []);

      return { Count, Rules };
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      return { Count: 0, Rules: [] };
    }
  };

  const fetchAllOrgSubs = async () => {
    try {
      const users = currentOrg?.Users.some(
        ({ _id }) => _id === currentUser?._id
      )
        ? currentOrg.Users
        : currentOrg?.Users?.concat?.(currentUser);

      const subsCommonParams = {
        ...commonSubscriptionsParams,
        gatewayIdFilter,
      };
      const { Count } =
        await subscriptionService.fetchUserSubscriptionsToAssetMetrics({
          params: subsCommonParams,
          cancelToken: source.token,
        });
      if (!Count) return new Map(users?.map?.(({ _id }) => [_id, []]));

      const reqCount = Math.ceil(Count / limit),
        reqProms = new Array(reqCount).fill(null).map((_, i) =>
          subscriptionService
            .fetchUserSubscriptionsToAsset({
              params: { ...subsCommonParams, limit, page: i + 1 },
              cancelToken: source.token,
            })
            .then(({ Subscriptions }) => Subscriptions)
        ),
        Subscriptions = (await Promise.all(reqProms)).flat();

      const subsMaps = Subscriptions.reduce(
        (acc, sub) => {
          const subString = `${sub.RuleId}:${sub.Notification}:${sub.Id}`;
          if (isValidNotNilUuid(sub?.UserId)) {
            const existingUserSubs = acc.platformSubs.get(sub.UserId) || [];
            acc.platformSubs.set(sub.UserId, [...existingUserSubs, subString]);
            return acc;
          }
          const existingSubs = acc.nonPlatformSubs.get(sub.Destination) || [];
          acc.nonPlatformSubs.set(sub.Destination, [
            ...existingSubs,
            subString,
          ]);
          return acc;
        },
        { nonPlatformSubs: new Map(), platformSubs: new Map() }
      );

      const subs =
        users?.map?.(({ _id } = {}) => {
          return [_id, subsMaps.platformSubs.get(_id) || []];
        }) ?? [];
      return new Map([...subs, ...subsMaps.nonPlatformSubs]);
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      return new Map();
    }
  };

  const handleAddSubscription = async (subString) => {
    try {
      const [RuleId, Notification] = subString.split(':'),
        { _id: UserId, Destination } = state.selectedUser,
        isPlatformSub = isValidNotNilUuid(UserId),
        conditionalData = isPlatformSub ? { UserId } : { Destination },
        Trigger =
          RuleId === '*'
            ? 'dm1'
            : maintenanceReminderIdSet.has(RuleId)
              ? 'maintenance'
              : 'rule',
        data = {
          ...conditionalData,
          RuleId,
          Notification,
          GatewayId: gatewayIdFilter,
          Trigger,
        },
        params = { orgID };

      const { Id } = await subscriptionService.postSubscription({
        params,
        data,
        cancelToken: source.token,
      });
      const subCopy = new Map(state.subscriptions);
      const existingSubs =
        subCopy.get(isPlatformSub ? UserId : Destination) || [];
      const update = [...existingSubs, `${subString}:${Id}`];
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
        subscriptions: subCopy.set(
          isPlatformSub ? UserId : Destination,
          update
        ),
      }));
      handleSubscriptionAlert('Added');
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const handleRemoveSubscription = async (subString) => {
    try {
      const { _id: UserId, Destination } = state.selectedUser;
      const isPlatformSub = isValidNotNilUuid(UserId);
      const selectedUserSubs =
        state.subscriptions.get(isPlatformSub ? UserId : Destination) || [];
      const sub = selectedUserSubs.find((s) => s.includes(subString));
      const [, , subscriptionId] = sub.split(':');
      const params = { orgID };
      await subscriptionService.deleteSubscription({
        subscriptionId,
        params,
        cancelToken: source.token,
      });
      const subCopy = new Map(state.subscriptions);

      const update = subCopy
        .get(isPlatformSub ? UserId : Destination)
        .filter((s) => !s.includes(subString));
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
        subscriptions: subCopy.set(
          isPlatformSub ? UserId : Destination,
          update
        ),
      }));
      handleSubscriptionAlert('Removed');
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const handleSubscriptionClick = (subString) => {
    try {
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: true,
      }));
      const userSubs =
        state.subscriptions.get(
          state.selectedUser?._id || state.selectedUser?.Destination
        ) || [];
      const subExists = userSubs.some((s) => s.includes(subString));
      subExists
        ? handleRemoveSubscription(subString)
        : handleAddSubscription(subString);
    } catch (err) {
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const fetchSelectedUserSubscriptions = async () => {
    try {
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: true,
      }));
      const { _id: userIdFilter, Destination: destinationFilter } =
        state.selectedUser;
      const conditionalParam = userIdFilter
        ? { userIdFilter }
        : { destinationFilter };
      const commonParams = {
        ...conditionalParam,
        gatewayIdFilter,
        orgID,
        orgIdFilter: orgID,
      };
      const { Count } =
        await subscriptionService.fetchUserSubscriptionsToAssetMetrics({
          params: commonParams,
          cancelToken: source.token,
        });
      if (!Count) {
        return updateState((prevState) => ({
          ...prevState,
          subscriptionPending: false,
          subscriptions: new Map(state.subscriptions).set(
            userIdFilter || destinationFilter,
            []
          ),
        }));
      }

      const reqCount = Math.ceil(Count / limit),
        reqProms = new Array(reqCount).fill(null).map((_, i) =>
          subscriptionService
            .fetchUserSubscriptionsToAsset({
              params: { ...commonParams, limit, page: i + 1 },
              cancelToken: source.token,
            })
            .then(({ Subscriptions }) => Subscriptions)
        ),
        Subscriptions = (await Promise.all(reqProms)).flat();

      const subs = Subscriptions.map(
        (s) => `${s.RuleId}:${s.Notification}:${s.Id}`
      );
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
        subscriptions: new Map(state.subscriptions).set(
          userIdFilter || destinationFilter,
          subs
        ),
      }));
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const handleBatchClick = async (Notification, SourceType) => {
    try {
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: true,
      }));
      const params = { orgID };
      const { _id: UserId, Destination } = state.selectedUser;
      const isPlatformSub = isValidNotNilUuid(UserId);
      const conditionalData = isPlatformSub ? { UserId } : { Destination };
      const data = {
        ...conditionalData,
        Notification,
        SourceId: gatewayIdFilter,
        SourceType,
      };
      const cancelToken = source.token;

      const userSubs =
        state.subscriptions.get(isPlatformSub ? UserId : Destination) || [];
      const isUnsubscribe = userSubs.some((s) => {
        const [ruleid, notification] = s.split(':');
        switch (true) {
          case ruleid === '*':
            return false;
          case SourceType === 'gateway':
            return (
              !maintenanceReminderIdSet.has(ruleid) &&
              notification === Notification
            );
          case SourceType === 'maintenance-gateway':
            return (
              maintenanceReminderIdSet.has(ruleid) &&
              notification === Notification
            );
          default:
            return false;
        }
      });

      const serviceMethod = isUnsubscribe ? 'unsubscribeAll' : 'subscribeAll';
      await subscriptionService[serviceMethod]({ params, data, cancelToken });

      fetchSelectedUserSubscriptions();
      handleSubscriptionAlert(isUnsubscribe ? 'Removed' : 'Added');
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const handleNonPlatformSubAdded = async (destinations) => {
    try {
      updateState((prevState) => ({ ...prevState, subscriptionPending: true }));
      const commonParams = { gatewayIdFilter, orgID };
      const subRequests = destinations.map(async (d) => {
        const params = { ...commonParams, destinationFilter: d };
        const { Count } =
          await subscriptionService.fetchUserSubscriptionsToAssetMetrics({
            params,
            cancelToken: source.token,
          });
        if (!Count) return [d, []];

        const { Subscriptions } =
          await subscriptionService.fetchUserSubscriptionsToAsset({
            params: { ...params, limit: Count },
            cancelToken: source.token,
          });

        const subs = Subscriptions.map(
          (s) => `${s.RuleId}:${s.Notification}:${s.Id}`
        );
        return [d, subs];
      });

      const update = await Promise.all(subRequests);
      updateState((prevState) => ({
        ...prevState,
        showSubscribeNonPlatformUserModal: false,
        subscriptionPending: false,
        subscriptions: new Map([...update, ...state.subscriptions]),
      }));
      handleSubscriptionAlert('Added');
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const handleNonPlatformSubRemove = async (Destination) => {
    try {
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: Destination,
      }));
      const subscriptions = new Map(state.subscriptions);

      const cancelToken = source.token;
      const data = {
        Destination,
        Notification: Destination.includes('@') ? 'email' : 'sms',
        SourceId: gatewayIdFilter,
        SourceType: 'gateway',
      };
      const params = { orgID };
      const unsubFromRules = subscriptionService.unsubscribeAll({
        cancelToken,
        data,
        params,
      });

      const userSubs = subscriptions.get(Destination) || [];
      const dmSub = userSubs.find((s) => s.startsWith('*')) || '';
      const [, , subscriptionId] = dmSub.split(':');
      const unsubFromDM = subscriptionId
        ? subscriptionService.deleteSubscription({
            cancelToken,
            params,
            subscriptionId,
          })
        : Promise.resolve();

      await Promise.all([unsubFromDM, unsubFromRules]);
      subscriptions.delete(Destination);

      const removingSelectedUser =
        state.selectedUser?.Destination === Destination;
      const conditionalSelectedUser = removingSelectedUser
        ? { selectedUser: currentUser }
        : {};
      updateState((prevState) => ({
        ...prevState,
        ...conditionalSelectedUser,
        subscriptionPending: false,
        subscriptions,
      }));
      if (removingSelectedUser && currentUserRef?.current) {
        currentUserRef.current.scrollIntoView();
      }

      handleSubscriptionAlert('Removed');
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
      updateState((prevState) => ({
        ...prevState,
        subscriptionPending: false,
      }));
    }
  };

  const setSelectedUser = (selectedUser) => {
    updateState((prevState) => ({ ...prevState, selectedUser }));
  };

  const toggleNonPlatformSubscriptionModal = () => {
    updateState((prevState) => ({
      ...prevState,
      showSubscribeNonPlatformUserModal:
        !state.showSubscribeNonPlatformUserModal,
    }));
  };

  const fetchAllInstancesRulesAndSubs = async () => {
    const [maintenanceState, ruleState, subscriptions] = await Promise.all([
      fetchAllReminderInstances(),
      fetchAllTemplateRules(),
      fetchAllOrgSubs(),
    ]);

    updateState((prevState) => ({
      ...prevState,
      maintenanceState,
      ruleState,
      subscriptionPending: false,
      subscriptions,
    }));
  };

  useEffect(() => {
    if (!sourceIdFilter) return;
    fetchAllInstancesRulesAndSubs();
  }, [sourceIdFilter]);

  useEffect(() => {
    const { _id, firstName, lastName, phoneNumber, settings } = currentUser;
    if (!_id) return;

    setSelectedUser({ _id, firstName, lastName, phoneNumber, settings });
    return source.cancel;
  }, [currentUser]);

  return (
    <AssetSubscriptionsDisplay
      {...props}
      {...state}
      currentOrg={currentOrg}
      currentUser={currentUser}
      currentUserRef={currentUserRef}
      error={error}
      handleBatchClick={handleBatchClick}
      handleNonPlatformSubAdded={handleNonPlatformSubAdded}
      handleNonPlatformSubRemove={handleNonPlatformSubRemove}
      handleSubscriptionClick={handleSubscriptionClick}
      setSelectedUser={setSelectedUser}
      templateID={sourceIdFilter}
      toggleNonPlatformSubscriptionModal={toggleNonPlatformSubscriptionModal}
    />
  );
}

AssetSubscriptionsContainer.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    orgID: PropTypes.string,
    gtemplate: PropTypes.object,
    gtemplateID: PropTypes.string,
  }),
  handleNonPlatformSubAdded: PropTypes.func,
  handleSubscriptionAlert: PropTypes.func,
};

AssetSubscriptionsContainer.defaultProps = { asset: {} };
